import { useId, useState, type RefObject } from 'react';
import { useLocale } from '@/i18n';
import { copperButton, gameColors } from '@/shared/ui/game-theme';

/** A narrow presentation port cannot access hero, auth or transport data. */
interface DiagnosticEngine { renderer: { captureDiagnostics(): unknown } }
export function GroundDiagnosticsContainer({ engineRef }: { engineRef: RefObject<DiagnosticEngine | null> }) {
  const { locale } = useLocale();
  const ru = locale === 'ru';
  const id = useId();
  const [report, setReport] = useState('');
  const [status, setStatus] = useState('');
  const [copying, setCopying] = useState(false);
  const collect = () => {
    try {
      const renderer = engineRef.current?.renderer;
      if (!renderer) throw new Error('Renderer is unavailable');
      setReport(JSON.stringify(renderer.captureDiagnostics(), null, 2));
      setStatus(ru ? 'Отчёт собран. Можно скопировать.' : 'Report captured. You can copy it.');
    } catch (error) {
      setReport(JSON.stringify({ errors: [error instanceof Error ? error.message : String(error)] }, null, 2));
      setStatus(ru ? 'В отчёте указана ошибка сбора.' : 'The report contains a capture error.');
    }
  };
  const copy = async () => {
    setCopying(true);
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(report);
      setStatus(ru ? 'Отчёт скопирован.' : 'Report copied.');
    } catch {
      setStatus(ru ? 'Не удалось скопировать. Выделите текст отчёта и скопируйте вручную.' : 'Could not copy. Select the report text and copy it manually.');
    } finally { setCopying(false); }
  };
  return <section aria-labelledby={`${id}-heading`} style={{ marginTop: 16 }}>
    <h3 id={`${id}-heading`} style={{ fontSize: 14 }}>{ru ? 'Диагностика карты' : 'Map diagnostics'}</h3>
    <p>{ru ? 'Если земля исчезла после масштаба, соберите отчёт до закрытия игры. Он останется здесь; отправьте его разработчику вручную.' : 'If ground disappears after zooming, capture a report before closing the game. It stays here; share it with the developer manually.'}</p>
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      <button type="button" style={{ ...copperButton, minHeight: 44, padding: '8px 12px' }} disabled={copying} onClick={collect}>{ru ? 'Собрать отчёт' : 'Capture report'}</button>
      <button type="button" style={{ ...copperButton, minHeight: 44, padding: '8px 12px' }} disabled={!report || copying} onClick={() => { void copy(); }}>{ru ? 'Скопировать' : 'Copy report'}</button>
    </div>
    <p role="status" aria-live="polite">{status}</p>
    {report && <><label htmlFor={`${id}-report`}>{ru ? 'Текст отчёта' : 'Report text'}</label>
      <textarea id={`${id}-report`} readOnly value={report} spellCheck={false} rows={8}
        style={{ width: '100%', boxSizing: 'border-box', fontSize: 11, background: '#141622', color: gameColors.text }} /></>}
  </section>;
}
